<template>
  <sidebar-row border-bottom class="pt-2">
    <template v-if="lightingSituationFeaturesLoaded">
      <v-row no-gutters>
        <base-button
          translation-path="sidebar.masterplanStreets.manageLightingSituation"
          @click="manageLightingSituation"
        />
      </v-row>
      <v-row no-gutters class="pt-3 pb-5">
        <v-col cols="12">
          <ribbon-list
            :listItems="lightingSituationFeatures"
            item-title-key="id"
            style-type="raw"
            disable-paddings
            :toggleable="false"
            fixed-height
          >
            <template #default="{ item }">
              <v-col
                cols="auto"
                :style="`background-color: ${item.color}; height: 37px; width: 6px`"
                class="masterplan-street-color-badge"
              />
              <v-col cols="auto" class="text-body-2 pl-3">
                {{ item.text }}
              </v-col>
              <v-spacer />
              <v-col cols="auto" class="pr-3" v-if="item.badge">
                <v-chip
                  :color="item.badgeColor"
                  label
                  small
                  :class="{
                    'white--text': !$_isHexColorLight(item.badgeColor),
                    'font--text': $_isHexColorLight(item.badgeColor),
                  }"
                >
                  {{ item.badge }}
                </v-chip>
              </v-col>
            </template>
          </ribbon-list>
        </v-col>
      </v-row>
      <masterplan-streets-widget-dialog
        v-if="isManageLightingSituationLoaded"
        :is-visible.sync="isManageLightingSituationVisible"
        :fid="fid"
        :features="lightingSituationFeatures"
        :features-mapping-function="getMappedLightingSituationFeatures"
        :type-attribute-dict="typeAttributeDict"
        @onSave="onSave"
      />
    </template>
    <main-loader v-else class="py-5" />
  </sidebar-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import MainLoader from '@/components/MainLoader';
import RibbonList from '@/components/RibbonList';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'MasterplanStreetsWidget',
  components: {
    MainLoader,
    MasterplanStreetsWidgetDialog: () => import('@/components/MasterplanStreetsWidgetDialog'),
    RibbonList,
    SidebarRow,
  },
  props: {
    fid: {
      type: Number,
      required: true,
    },
  },
  computed: {
    dataSources: get('layers/metadata'),
    masterplanSituationsDataSourceMapping: get(
      'admin/modulesMapping@masterplan_2_streets@masterplan_situation_datasource'
    ),
    lightingSituationDataSourceName() {
      return this.masterplanSituationsDataSourceMapping?.datasource || '';
    },
    lightingSituationDataSource() {
      return this.dataSources[this.lightingSituationDataSourceName] || undefined;
    },
    lightingSituationAttributes() {
      return this.masterplanSituationsDataSourceMapping?.settings || {};
    },
  },
  data: () => ({
    lightingSituationFeatures: [],
    lightingSituationFeaturesLoaded: false,

    isManageLightingSituationLoaded: false,
    isManageLightingSituationVisible: false,

    situationAttributeDict: undefined,
    typeAttributeDict: undefined,
  }),
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    manageLightingSituation() {
      this.isManageLightingSituationLoaded = true;
      this.isManageLightingSituationVisible = true;
    },
    async getLightingSituationFeatures({ fid = this.fid, init = false } = {}) {
      try {
        if (init) this.lightingSituationFeaturesLoaded = false;
        const { street_id_attribute: streeIdAttribute } = this.lightingSituationAttributes;
        const r = await this.getDataSourceFeatures({
          dataSource: this.lightingSituationDataSourceName,
          filters: {
            $EQUAL: {
              [this.lightingSituationDataSource?.attributes_use_datasource_qualified_names
                ? streeIdAttribute
                : `${this.lightingSituationDataSourceName}.${streeIdAttribute}`]: { value: fid },
            },
          },
        });
        this.lightingSituationFeatures = this.getMappedLightingSituationFeatures(
          JSON.parse(JSON.stringify(r?.features || []))
        );
      } finally {
        this.lightingSituationFeaturesLoaded = true;
      }
    },
    getMappedLightingSituationFeatures(features = []) {
      ['situation', 'type'].forEach(type => {
        if (!this[`${type}AttributeDict`]) {
          const attribute = this.lightingSituationDataSource?.attributes_schema.attributes?.find(
            attribute => attribute.name === this.lightingSituationAttributes[`${type}_attribute`]
          );
          this[`${type}AttributeDict`] =
            attribute?.allowed_values?.reduce((total, current, index) => {
              total[current] = attribute?.dict_colors[index];
              return total;
            }, {}) || {};
        }
      });
      const typesCount = {};
      return features
        .sort(
          (a, b) =>
            a.properties[this.lightingSituationAttributes.order_attribute] -
            b.properties[this.lightingSituationAttributes.order_attribute]
        )
        .map(feature => {
          const {
            [this.lightingSituationAttributes.situation_attribute]: situation,
            [this.lightingSituationAttributes.type_attribute]: type,
            [this.lightingSituationAttributes.class_attribute]: clas,
            [this.lightingSituationAttributes.calculation_attribute]: calculator,
          } = feature.properties;
          const calculatorJson = JSON.parse(calculator || null);
          const calculatorClass = calculatorJson?.[situation]?.class;
          if (typesCount[type]) typesCount[type]++;
          else typesCount[type] = 1;
          return {
            id: feature.id,
            properties: feature.properties,
            text: `${type} ${typesCount[type]}`,
            color: this.typeAttributeDict[type] || this.$_colors['primary'],
            ...(situation && (calculatorClass || calculatorClass === 0 || clas || clas === 0)
              ? { badge: `${situation}${clas || clas === 0 ? clas : calculatorClass}` }
              : {}),
            badgeColor: this.situationAttributeDict[situation] || this.$_colors['primary'],
            ...(calculatorClass || calculatorClass === 0 ? { calculatorBadge: `${situation}${calculatorClass}` } : {}),
          };
        });
    },
    onSave() {
      this.getLightingSituationFeatures();
    },
  },
  mounted() {
    this.getLightingSituationFeatures({ init: true });
  },
};
</script>
<style lang="scss" scoped>
.masterplan-street-color-badge {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
